import { useSyncExternalStore } from 'react';

import { isTouchLayout } from './adminLayoutMode';
import { isCoarsePointer } from '../../utils/ui/isCoarsePointer';

/**
 * `isTouch` for LIST-ROW primitives (KAN-947). Same answer as `useResponsiveLayout().isTouch`
 * (KAN-970: width under 1024, or a coarse pointer), but read through ONE shared subscription,
 * so a list of 500 checkboxes adds 2 window listeners, not 1,000. Use this only in a component
 * that renders many times per screen; everything else reads `useResponsiveLayout()`.
 */
const COARSE_QUERY = '(pointer: coarse)';

const listeners = new Set();
let detach = null;
// While anyone is subscribed the answer is cached and refreshed by the two events, so a list of
// 500 rows never calls matchMedia 500 times per render. Unsubscribed (first render, tests) it is
// always read fresh.
let cached = null;

function compute() {
  if (typeof window === 'undefined') return false;
  const w = window.innerWidth;
  return isTouchLayout(w < 768, w >= 768 && w < 1024, isCoarsePointer());
}

function read() {
  return listeners.size > 0 && cached !== null ? cached : compute();
}

function notify() {
  cached = compute();
  listeners.forEach((fn) => fn());
}

function attach() {
  window.addEventListener('resize', notify);
  let mql = null;
  try {
    mql = typeof window.matchMedia === 'function' ? window.matchMedia(COARSE_QUERY) : null;
  } catch {
    mql = null;
  }
  if (mql && typeof mql.addEventListener === 'function') mql.addEventListener('change', notify);
  else if (mql && typeof mql.addListener === 'function') mql.addListener(notify); // Safari < 14
  return () => {
    window.removeEventListener('resize', notify);
    if (mql && typeof mql.removeEventListener === 'function') mql.removeEventListener('change', notify);
    else if (mql && typeof mql.removeListener === 'function') mql.removeListener(notify);
  };
}

function subscribe(fn) {
  if (typeof window === 'undefined') return () => {};
  if (listeners.size === 0) {
    detach = attach();
    cached = compute();
  }
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) {
      cached = null;
      if (detach) detach();
      detach = null;
    }
  };
}

export function useIsTouch() {
  return useSyncExternalStore(subscribe, read, () => false);
}

export default useIsTouch;
